/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AddUserInOrganization } from '../models/AddUserInOrganization';
import type { AdminArchiveUserResponse } from '../models/AdminArchiveUserResponse';
import type { CurrentAccountUser } from '../models/CurrentAccountUser';
import type { CurrentOrganizationUser } from '../models/CurrentOrganizationUser';
import type { NotificationServiceSync } from '../models/NotificationServiceSync';
import type { NotificationServiceUser } from '../models/NotificationServiceUser';
import type { OrganizationUsers } from '../models/OrganizationUsers';
import type { PaginatedOrganizationUsersList } from '../models/PaginatedOrganizationUsersList';
import type { PaginatedUserListList } from '../models/PaginatedUserListList';
import type { PatchedUserEdit } from '../models/PatchedUserEdit';
import type { PatchedUserSerilaizer } from '../models/PatchedUserSerilaizer';
import type { SyncActiveDirectoryResponse } from '../models/SyncActiveDirectoryResponse';
import type { UserDomainLogin } from '../models/UserDomainLogin';
import type { UserEdit } from '../models/UserEdit';
import type { UserInvite } from '../models/UserInvite';
import type { UserInviteAccept } from '../models/UserInviteAccept';
import type { UserProfile } from '../models/UserProfile';
import type { UserRegister } from '../models/UserRegister';
import type { UserRegisterConfirm } from '../models/UserRegisterConfirm';
import type { UserRegisterStart } from '../models/UserRegisterStart';
import type { UserRetrieve } from '../models/UserRetrieve';
import type { UserSerilaizer } from '../models/UserSerilaizer';
import type { UserShort } from '../models/UserShort';
import type { UserVeryShort } from '../models/UserVeryShort';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class UsersService {

  /**
   * @param email 
   * @param ordering Which field to use when ordering the results.
   * @param organization 
   * @param page A page number within the paginated result set.
   * @param perPage Number of results to return per page.
   * @param search 
   * @param team 
   * @returns PaginatedUserListList 
   * @throws ApiError
   */
  public static usersList(
email?: string,
ordering?: string,
organization?: number,
page?: number,
perPage?: number,
search?: string,
team?: number,
): CancelablePromise<PaginatedUserListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/users/',
      query: {
        'email': email,
        'ordering': ordering,
        'organization': organization,
        'page': page,
        'per_page': perPage,
        'search': search,
        'team': team,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this Utilizator.
   * @returns UserRetrieve 
   * @throws ApiError
   */
  public static usersRetrieve(
id: number,
): CancelablePromise<UserRetrieve> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/users/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this Utilizator.
   * @param requestBody 
   * @returns UserSerilaizer 
   * @throws ApiError
   */
  public static usersPartialUpdate(
id: number,
requestBody?: PatchedUserSerilaizer,
): CancelablePromise<UserSerilaizer> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/users/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param requestBody 
   * @returns OrganizationUsers 
   * @throws ApiError
   */
  public static usersAddInOrganizationCreate(
requestBody: AddUserInOrganization,
): CancelablePromise<OrganizationUsers> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/users/add-in-organization/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param userId 
   * @returns AdminArchiveUserResponse 
   * @throws ApiError
   */
  public static usersArchiveCreate(
userId: number,
): CancelablePromise<AdminArchiveUserResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/users/archive/{user_id}/',
      path: {
        'user_id': userId,
      },
    });
  }

  /**
   * @deprecated
   * @param requestBody 
   * @returns UserShort 
   * @throws ApiError
   */
  public static usersEditUpdate(
requestBody: UserEdit,
): CancelablePromise<UserShort> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/users/edit/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param requestBody 
   * @returns UserShort 
   * @throws ApiError
   */
  public static usersEditPartialUpdate(
requestBody?: PatchedUserEdit,
): CancelablePromise<UserShort> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/users/edit/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param requestBody 
   * @param page A page number within the paginated result set.
   * @param perPage Number of results to return per page.
   * @returns PaginatedOrganizationUsersList 
   * @throws ApiError
   */
  public static usersInviteCreate(
requestBody: UserInvite,
page?: number,
perPage?: number,
): CancelablePromise<PaginatedOrganizationUsersList> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/users/invite/',
      query: {
        'page': page,
        'per_page': perPage,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param requestBody 
   * @returns UserShort 
   * @throws ApiError
   */
  public static usersInviteAcceptCreate(
requestBody: UserInviteAccept,
): CancelablePromise<UserShort> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/users/invite-accept/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @returns CurrentAccountUser 
   * @throws ApiError
   */
  public static usersLoginAdminCreate(): CancelablePromise<CurrentAccountUser> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/users/login/admin/',
    });
  }

  /**
   * @param requestBody 
   * @returns CurrentOrganizationUser 
   * @throws ApiError
   */
  public static usersLoginUserCreate(
requestBody: UserDomainLogin,
): CancelablePromise<CurrentOrganizationUser> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/users/login/user/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param requestBody 
   * @returns NotificationServiceUser 
   * @throws ApiError
   */
  public static usersNotificationServiceSyncCreate(
requestBody: NotificationServiceSync,
): CancelablePromise<NotificationServiceUser> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/users/notification-service-sync/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Validation pattern for service domain: `^[a-z]([a-z0-9]+-?)+[a-z0-9]$`
   * @param requestBody 
   * @returns UserProfile 
   * @throws ApiError
   */
  public static usersRegisterCreate(
requestBody: UserRegister,
): CancelablePromise<UserProfile> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/users/register/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Two-step simple registration. Confirm.
   * @param requestBody 
   * @returns UserVeryShort 
   * @throws ApiError
   */
  public static usersRegisterConfirmCreate(
requestBody: UserRegisterConfirm,
): CancelablePromise<UserVeryShort> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/users/register-confirm/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Two-step simple registration. Start.
 *
 * **reCAPTCHA required.** The `recaptcha_token` field must contain a token produced by reCAPTCHA with action `register`:
 * - Web clients: `grecaptcha.execute(SITE_KEY, {action: 'register'})`.
 * - iOS/Android clients: `client.execute(RecaptchaAction.custom('register'))` and send the `X-Platform: ios` or `X-Platform: android` HTTP header (without the header the request is verified as web).
   * @param requestBody 
   * @returns UserVeryShort 
   * @throws ApiError
   */
  public static usersRegisterStartCreate(
requestBody: UserRegisterStart,
): CancelablePromise<UserVeryShort> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/users/register-start/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * @param organizationId 
   * @returns SyncActiveDirectoryResponse 
   * @throws ApiError
   */
  public static usersSyncActiveDirectoryCreate(
organizationId: number,
): CancelablePromise<SyncActiveDirectoryResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/users/sync-active-directory/{organization_id}/',
      path: {
        'organization_id': organizationId,
      },
    });
  }

}
